import { CalendarEvent } from "@/Calendar";
import { EventListItem } from "./EventListItem";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useMemo, memo, useState } from "react";
import { cn } from "@/lib/utils";
import { Calendar, ChevronRight, ChevronLeft } from "lucide-react";

// Create a unified event type
export interface UnifiedEvent {
  id: string;
  date: string;
  title: string;
  country: string;
  impact: 'Low' | 'Medium' | 'High';
  time?: string;
  actual?: number;
  forecast?: number;
  previous?: number;
  unit?: string;
  score: number;
  currency: string;
  estimate?: number;
  change?: number;
  rawImpact?: string;
}

interface PaginatedEventsListProps {
  events: CalendarEvent[];
  selectedEvent: UnifiedEvent | null;
  onEventClick: (event: UnifiedEvent) => void;
  isLoading?: boolean;
  viewDate?: Date;
}

const EVENTS_PER_PAGE = 5;

const PaginatedEventsListComponent = ({ 
  events, 
  selectedEvent, 
  onEventClick, 
  isLoading,
  viewDate 
}: PaginatedEventsListProps) => {
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [currentPage, setCurrentPage] = useState(1);

  // Convert CalendarEvent to our unified format and filter for selected date
  const filteredDayEvents = useMemo(() => {
    if (!viewDate || events.length === 0) {
      // Default to today's events if no viewDate specified
      const defaultDate = new Date().toISOString().split('T')[0];
      return events.find(calendarEvent => calendarEvent.date === defaultDate) || null;
    }
    
    const viewDateStr = viewDate.toISOString().split('T')[0];
    return events.find(calendarEvent => calendarEvent.date === viewDateStr) || null;
  }, [events, viewDate]);

  // Convert EventDetails to UnifiedEvent and sort
  const sortedEvents = useMemo(() => {
    if (!filteredDayEvents) return [];
    
    return filteredDayEvents.events.map((eventDetails, index) => {
      const unifiedEvent: UnifiedEvent = {
        id: `${filteredDayEvents.date}-${index}`,
        date: filteredDayEvents.date,
        title: eventDetails.title || eventDetails.event || 'Untitled Event',
        country: eventDetails.country || 'Unknown',
        impact: (() => {
          const apiImpact = (eventDetails.impact || 'low').toLowerCase();
          if (apiImpact === 'high') return 'High';
          if (apiImpact === 'medium') return 'Medium';
          return 'Low';
        })() as 'Low' | 'Medium' | 'High',
        time: eventDetails.time,
        actual: eventDetails.actual,
        forecast: eventDetails.forecast,
        previous: eventDetails.previous,
        unit: eventDetails.unit,
        score: eventDetails.score || 0, // Use raw API score
        currency: 'USD',
        estimate: eventDetails.forecast,
        change: (eventDetails.actual || 0) - (eventDetails.previous || 0),
        rawImpact: eventDetails.impact || 'low'
      };

      return unifiedEvent;
    }).sort((a, b) => {
      // Primary sort: Impact level (High > Medium > Low)
      const impactOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
      const impactDiff = impactOrder[b.impact] - impactOrder[a.impact];
      if (impactDiff !== 0) return impactDiff;
      
      // Secondary sort: Score (higher first)
      const scoreDiff = b.score - a.score;
      if (scoreDiff !== 0) return scoreDiff;
      
      // Tertiary sort: If scores are very close, use actual values as tiebreaker
      if (a.actual !== undefined && b.actual !== undefined) {
        const actualDiff = Math.abs(b.actual - (b.estimate || b.forecast || 0)) - 
                          Math.abs(a.actual - (a.estimate || a.forecast || 0));
        if (Math.abs(actualDiff) > 0.01) return actualDiff;
      }
      
      // Quaternary sort: Time (earlier first, if available)
      if (a.time && b.time) {
        return a.time.localeCompare(b.time);
      }
      
      // Final sort: Title alphabetically
      return a.title.localeCompare(b.title);
    });
  }, [filteredDayEvents]);

  // Reset pagination when date changes
  const viewDateStr = useMemo(() => {
    return viewDate?.toISOString().split('T')[0];
  }, [viewDate]);

  useMemo(() => {
    setCurrentPage(1);
  }, [viewDateStr]);

  const handleShowMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handleShowLess = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const getVisibleEvents = () => {
    const endIndex = currentPage * EVENTS_PER_PAGE;
    return sortedEvents.slice(0, endIndex);
  };

  const hasMoreEvents = () => {
    return sortedEvents.length > currentPage * EVENTS_PER_PAGE;
  };

  const hasPreviousEvents = () => {
    return currentPage > 1;
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-6 w-40" />
        <div className="space-y-2">
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="w-full p-4 rounded-lg border border-white/5"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <div className="flex items-center gap-1">
                      <Skeleton className="h-3 w-3 rounded-sm" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-14" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!filteredDayEvents || sortedEvents.length === 0) {
    const displayDate = viewDate || new Date();
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center p-4">
        <div className="rounded-full bg-blue-950/30 p-3 mb-3">
          <Calendar className="h-6 w-6 text-blue-400" />
        </div>
        <h3 className="text-lg font-medium text-white/80 mb-1">No events found</h3>
        <p className="text-sm text-white/60 max-w-xs">
          No economic events scheduled for{" "}
          {displayDate.toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'long', 
            day: 'numeric'
          })}.
        </p>
      </div>
    );
  }

  const visibleEvents = getVisibleEvents();
  const showMoreButton = hasMoreEvents();
  const showLessButton = hasPreviousEvents();
  const totalEvents = sortedEvents.length;
  const isToday = filteredDayEvents.date === today;

  return (
    <div className="p-4 space-y-4">
      {/* Day Header */}
      <div className={cn(
        "pb-3 border-b border-border/50",
        isToday && "bg-gradient-to-r from-blue-950/30 to-transparent rounded-lg px-3 py-2"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className={cn(
              "h-4 w-4",
              isToday ? "text-blue-400" : "text-blue-400/60"
            )} />
            <h3 className={cn(
              "font-medium",
              isToday && "text-blue-300"
            )}>
              {new Date(filteredDayEvents.date).toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
              {isToday && " (Today)"}
            </h3>
          </div>
          
          {totalEvents > EVENTS_PER_PAGE && (
            <div className="text-xs text-white/60">
              {Math.min(currentPage * EVENTS_PER_PAGE, totalEvents)} of {totalEvents}
            </div>
          )}
        </div>
      </div>
      
      {/* Events List */}
      <div className="space-y-2">
        {visibleEvents.map((event) => (
          <EventListItem
            key={event.id}
            event={event}
            isSelected={selectedEvent?.id === event.id}
            onClick={onEventClick}
            isToday={isToday}
          />
        ))}
      </div>

      {/* Pagination Controls */}
      {(showMoreButton || showLessButton) && (
        <div className="flex items-center justify-center gap-2 py-2">
          {showLessButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShowLess}
              className="text-xs text-white/70 hover:text-white hover:bg-white/10"
            >
              <ChevronLeft className="h-3 w-3 mr-1" />
              Show Less
            </Button>
          )}
          
          {showMoreButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShowMore}
              className="text-xs text-white/70 hover:text-white hover:bg-white/10"
            >
              Show More
              <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Implement proper comparison function for memo
const arePropsEqual = (prevProps: PaginatedEventsListProps, nextProps: PaginatedEventsListProps) => {
  return (
    prevProps.events === nextProps.events &&
    prevProps.selectedEvent?.id === nextProps.selectedEvent?.id &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.viewDate?.getTime() === nextProps.viewDate?.getTime()
  );
};

export const PaginatedEventsList = memo(PaginatedEventsListComponent, arePropsEqual); 